import type { SkillData } from '../spacetime/modules/SkillModule';
import { ActionTooltip, parseInputList } from './ActionTooltip';
import { localizeSkillName, localizeSkillDescription } from '../i18n/content';
import { t } from '../i18n';
import { createSkillTagStatLine } from './skillTags';
import { createSkillEmojiBox, isGreaterSkill } from './skillVariants';

export function showSkillTooltip(tooltip: ActionTooltip, skill: SkillData, anchorEl: HTMLElement): void {
    const statLines = [];
    if (skill.damage > 0) statLines.push(t('character.statDmg', { value: skill.damage }));
    if (skill.range > 0) statLines.push(t('character.statRange', { value: skill.range }));
    if (skill.radius > 0) statLines.push(`Radius: ${skill.radius}`);
    if (skill.durationTicks > 0) statLines.push(t('character.statDuration', { value: skill.durationTicks }));
    if (skill.cooldownTicks > 0) statLines.push(t('character.statCooldown', { value: skill.cooldownTicks }));
    if (skill.staminaCost > 0) statLines.push(`Stamina: ${skill.staminaCost}`);
    const inputs = parseInputList(skill.inputsJson);
    if (inputs.length > 0) statLines.push(`Inputs: ${inputs.join(', ')}`);
    if (skill.tags.length > 0) statLines.push(createSkillTagStatLine(skill.tags));
    tooltip.showAbove(
        { title: localizeSkillName(skill.skillId, skill.displayName), description: localizeSkillDescription(skill.skillId, skill.description), statLines },
        anchorEl,
    );
}

export interface SkillSlotHandlers {
    onClick?: () => void;
    onTooltipShow?: (skill: SkillData, anchorEl: HTMLElement) => void;
    onTooltipHide?: () => void;
}

/**
 * Builds a `.skill-bar-slot.filled` element identical to what SkillBar renders.
 * @param slotNumber  1-based label shown in the corner (pass 0 to omit)
 */
export function buildSkillSlotEl(
    skill: SkillData,
    currentTick: number,
    slotNumber: number,
    handlers: SkillSlotHandlers = {},
): { el: HTMLElement; onCooldown: boolean } {
    const slotEl = document.createElement('div');
    slotEl.className = 'skill-bar-slot filled';

    if (slotNumber > 0) {
        const numberEl = document.createElement('span');
        numberEl.className = 'slot-number';
        numberEl.textContent = `${slotNumber}`;
        slotEl.appendChild(numberEl);
    }

    slotEl.appendChild(createSkillEmojiBox(skill.emoji, isGreaterSkill(skill.skillId)));

    const ticksSinceUse = currentTick - skill.lastUsedTick;
    const onCooldown = ticksSinceUse < skill.cooldownTicks;
    if (onCooldown) {
        const remaining = skill.cooldownTicks - ticksSinceUse;
        const overlay = document.createElement('div');
        overlay.className = 'cooldown-overlay';
        overlay.textContent = String(remaining);
        slotEl.appendChild(overlay);
    }

    if (handlers.onClick && !onCooldown) {
        slotEl.addEventListener('click', handlers.onClick);
    }
    if (onCooldown) {
        slotEl.style.opacity = '0.5';
        slotEl.style.cursor = 'default';
    }

    if (handlers.onTooltipShow) {
        slotEl.addEventListener('mouseenter', () => handlers.onTooltipShow!(skill, slotEl));
    }
    if (handlers.onTooltipHide) {
        slotEl.addEventListener('mouseleave', handlers.onTooltipHide);
        slotEl.addEventListener('mousedown', handlers.onTooltipHide);
    }

    return { el: slotEl, onCooldown };
}
